import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Clock3, FileCode2, FileText, Plus, RefreshCw, Send, Trash2, Undo2 } from 'lucide-react';
import {
  GROUPINGS, PAYMENT_METHODS,
  INVOICE_LANGUAGES,
  LINE_UNITS,
  PAYMENT_SOURCES,
  VAT_CODES,
  VAT_SCHEMES,
  approveInvoice,
  cancelInvoice,
  createInvoiceLine,
  createPayment,
  creditInvoice,
  deleteInvoiceLine,
  deletePayment,
  markInvoiceSent,
  markInvoiceTransferred,
  renderInvoiceDocuments,
  sendInvoiceMail,
  submitInvoice,
  updateInvoice,
  withdrawInvoice,
} from '@mkcore/billing/schema';
import { today } from '@mkcore/shared';
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Textarea, Tr } from '@mkcore/ui';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { ReminderButton } from '@/features/collab/reminders';
import { ApiError } from '@/lib/api';
import { formatDate, formatDateTime, formatDuration, formatMoney, formatNumber } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { invoiceQuery, paymentsQuery } from '@/features/billing/queries';
import { InvoiceKindLabel, InvoiceStatusBadge } from '@/features/billing/labels';
import { centsToInput, inputToCents } from '@/features/engagements/money';
import { DecideDialog } from '@/features/approvals/decide-dialog';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/billing/invoices/$id')({ component: InvoiceDetail });

type Header = { issueDate: string; paymentTermDays: string; language: string; vatScheme: string; grouping: string; reference: string; purchaseOrder: string; paymentMethod: string; notes: string };

/** One invoice (CO-0339): header, lines, totals and the actions of its status; drafts are editable, the rest is frozen. */
function InvoiceDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const me = useMe();
  const canUpdate = useCan('billing:update');
  const canCreate = useCan('billing:create');
  const canApprove = useCan('billing:approve');
  const canManage = useCan('billing:manage');
  const q = useQuery(invoiceQuery(id));
  const [form, setForm] = useState<Header | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(false);
  const [decide, setDecide] = useState<'reject' | 'revise' | null>(null);
  useEffect(() => setForm(null), [id, q.data?.version]);
  const invalidate = [['billing'], ['time'], ['approval']];
  const save = useActionMutation(updateInvoice, { invalidate, success: t('common.saved'), silentErrors: true });
  const submit = useActionMutation(submitInvoice, { invalidate, success: t('invoices.submitted') });
  const approve = useActionMutation(approveInvoice, { invalidate, success: t('invoices.approved') });
  const withdraw = useActionMutation(withdrawInvoice, { invalidate, success: t('invoices.withdrawn') });
  const cancel = useActionMutation(cancelInvoice, { invalidate, success: t('invoices.cancelled') });
  const sent = useActionMutation(markInvoiceSent, { invalidate, success: t('invoices.markedSent') });
  const credit = useActionMutation(creditInvoice, { invalidate, success: t('invoices.creditCreated') });
  const removeLine = useActionMutation(deleteInvoiceLine, { invalidate });
  const render = useActionMutation(renderInvoiceDocuments, { invalidate, success: t('invoices.documents.made') });
  const mail = useActionMutation(sendInvoiceMail, { invalidate, success: t('invoices.mail.started') });
  const transferred = useActionMutation(markInvoiceTransferred, { invalidate, success: t('invoices.giroo.recorded') });

  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const inv = q.data;
  const draft = inv.status === 'draft';
  const editable = draft && canUpdate;
  const definitive = ['approved', 'sent', 'partially_paid', 'paid', 'credited'].includes(inv.status);
  const hasTime = inv.lines.some((l) => l.source === 'time');
  // The documents are files (REST only): the browser opens them with the session cookie (CO-0338, CO-0344, CO-0345).
  const openDocument = (kind: 'pdf' | 'ubl' | 'specification') => window.open(`/api/v1/billing/invoices/${id}/${kind}`, '_blank', 'noopener');
  const f: Header = form ?? {
    issueDate: inv.issueDate ?? '',
    paymentTermDays: String(inv.paymentTermDays),
    language: inv.language,
    vatScheme: inv.vatScheme,
    grouping: inv.grouping,
    reference: inv.reference,
    purchaseOrder: inv.purchaseOrder,
    paymentMethod: inv.paymentMethod,
    notes: inv.notes,
  };
  const set = (patch: Partial<Header>) => setForm({ ...f, ...patch });
  const saveHeader = () =>
    save.mutate(
      {
        input: {
          id,
          issueDate: f.issueDate || null,
          paymentTermDays: Number(f.paymentTermDays),
          language: f.language as (typeof INVOICE_LANGUAGES)[number],
          vatScheme: f.vatScheme as (typeof VAT_SCHEMES)[number],
          grouping: f.grouping as (typeof GROUPINGS)[number],
          reference: f.reference,
          purchaseOrder: f.purchaseOrder,
          paymentMethod: f.paymentMethod as (typeof PAYMENT_METHODS)[number],
          notes: f.notes,
        },
        ifMatch: inv.version,
      },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) }), onSuccess: () => setErrors({}) },
    );
  const party = (p: NonNullable<typeof inv.supplier>) => (
    <address className="text-sm not-italic text-fg-2">
      <span className="block font-bold text-fg-1">{p.name}</span>
      {p.street && <span className="block">{p.street}</span>}
      {(p.postalCode || p.city) && <span className="block">{[p.postalCode, p.city].filter(Boolean).join(' ')}</span>}
      {p.vatNumber && <span className="block">{t('org.fields.vatNumber')}: {p.vatNumber}</span>}
      {p.registrationNumber && <span className="block">{t('org.fields.registrationNumber')}: {p.registrationNumber}</span>}
      {p.iban && <span className="block">IBAN {p.iban}</span>}
      {p.email && <span className="block">{p.email}</span>}
    </address>
  );
  return (
    <>
      <Link to="/billing/invoices" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('invoices.title')}
      </Link>
      <PageHeader
        eyebrow={t(`invoices.kind.${inv.kind}`)}
        title={
          <span className="flex flex-wrap items-center gap-2">
            <InvoiceKindLabel kind={inv.kind} number={inv.number} />
            <InvoiceStatusBadge status={inv.status} />
          </span>
        }
        description={
          <span>
            {inv.organisationName}
            {inv.engagementName ? ` · ${inv.engagementName}` : ''}
            {inv.creditsInvoiceId && (
              <>
                {' · '}
                <Link to="/billing/invoices/$id" params={{ id: inv.creditsInvoiceId }} className="underline">
                  {t('invoices.credits', { number: inv.creditsInvoiceNumber ?? '' })}
                </Link>
              </>
            )}
          </span>
        }
        actions={
          <span className="flex flex-wrap gap-2">
            <ReminderButton targetType="invoice" targetId={inv.id} title={`${t('invoices.invoice')} ${inv.number ?? ''}`.trim()} path={`/billing/invoices/${inv.id}`} dates={[{ label: t('invoices.fields.dueDate'), date: inv.dueDate }]} />
            <Button variant="ghost" onClick={() => openDocument('pdf')}>
              <FileText /> {t('invoices.pdf')}
            </Button>
            {hasTime && (
              <Button variant="ghost" onClick={() => openDocument('specification')}>
                <Clock3 /> {t('invoices.specification')}
              </Button>
            )}
            {definitive && (
              <Button variant="ghost" onClick={() => openDocument('ubl')}>
                <FileCode2 /> {t('invoices.ubl')}
              </Button>
            )}
            {draft && canUpdate && (
              <Button variant="secondary" loading={submit.isPending} disabled={!inv.lines.length} onClick={() => submit.mutate({ input: { id } })}>
                <Send /> {t('invoices.submit')}
              </Button>
            )}
            {(draft || inv.status === 'pending') && canApprove && (
              <ConfirmButton
                variant="primary"
                icon={<Check />}
                label={t('invoices.approve')}
                title={t('invoices.approveTitle')}
                description={t('invoices.approveText')}
                disabled={!inv.lines.length}
                onConfirm={() => approve.mutateAsync({ input: { id } })}
              />
            )}
            {inv.status === 'pending' && canApprove && inv.approvalId && (
              <>
                <Button variant="secondary" onClick={() => setDecide('revise')}>
                  {t('approvals.action.revise')}
                </Button>
                <Button variant="ghost" onClick={() => setDecide('reject')}>
                  {t('approvals.action.reject')}
                </Button>
              </>
            )}
            {inv.status === 'pending' && canUpdate && (
              <Button variant="ghost" loading={withdraw.isPending} onClick={() => withdraw.mutate({ input: { id } })}>
                <Undo2 /> {t('invoices.withdraw')}
              </Button>
            )}
            {(draft || inv.status === 'pending') && canUpdate && (
              <ConfirmButton variant="ghost" icon={<Trash2 />} label={t('invoices.cancel')} title={t('invoices.cancelTitle')} description={t('invoices.cancelText')} onConfirm={() => cancel.mutateAsync({ input: { id } })} />
            )}
            {definitive && canUpdate && inv.mailStatus !== 'drafted' && (
              <ConfirmButton
                variant="secondary"
                icon={<Send />}
                label={t('invoices.mail.send')}
                title={t('invoices.mail.sendTitle')}
                description={t('invoices.mail.sendText', { to: inv.customer?.email || '–' })}
                onConfirm={() => mail.mutateAsync({ input: { id } })}
              />
            )}
            {inv.status === 'approved' && canUpdate && (
              <Button variant="secondary" loading={sent.isPending} onClick={() => sent.mutate({ input: { id } })}>
                <Send /> {t('invoices.markSent')}
              </Button>
            )}
            {inv.kind === 'invoice' && ['approved', 'sent', 'partially_paid', 'paid'].includes(inv.status) && canUpdate && (
              <Button variant="secondary" onClick={() => setPaying(true)}>
                {t('invoices.registerPayment')}
              </Button>
            )}
            {inv.kind === 'invoice' && ['approved', 'sent', 'partially_paid', 'paid', 'credited'].includes(inv.status) && canCreate && inv.creditedCents < inv.totalCents && (
              <ConfirmButton variant="ghost" label={t('invoices.credit')} title={t('invoices.creditTitle')} description={t('invoices.creditText')} onConfirm={() => credit.mutateAsync({ input: { id } })} />
            )}
          </span>
        }
      />
      {inv.warnings.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {inv.warnings.map((w) => (
            <Badge key={w} tone="warning">
              {t(`invoices.warnings.${w}`)}
            </Badge>
          ))}
        </div>
      )}
      {definitive && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-fg-2" data-testid="invoice-documents">
          <Badge tone={inv.ublStatus === 'valid' ? 'success' : inv.ublStatus === 'invalid' ? 'danger' : 'neutral'}>{t(`invoices.ublStatus.${inv.ublStatus}`)}</Badge>
          <span>{inv.documentsAt ? t('invoices.documents.at', { date: formatDateTime(inv.documentsAt) }) : t('invoices.documents.pending')}</span>
          {canUpdate && (
            <Button size="sm" variant="ghost" loading={render.isPending} onClick={() => render.mutate({ input: { id } })}>
              <RefreshCw /> {t('invoices.rerender')}
            </Button>
          )}
          <Badge tone={inv.mailStatus === 'sent' ? 'success' : inv.mailStatus === 'failed' || inv.mailStatus === 'rejected' ? 'danger' : 'neutral'}>{t(`invoices.mail.status.${inv.mailStatus}`)}</Badge>
          {inv.mailStatus === 'drafted' && inv.mailDraftLink && (
            <a href={inv.mailDraftLink} target="_blank" rel="noopener noreferrer" className="underline">
              {t('invoices.mail.draft')}
            </a>
          )}
          {inv.mailSentAt && <span>{t('invoices.mail.sentAt', { date: formatDateTime(inv.mailSentAt), to: inv.mailTo.join(', ') })}</span>}
          {inv.transferredAt ? (
            <span>{t('invoices.giroo.at', { date: formatDateTime(inv.transferredAt) })}</span>
          ) : (
            canUpdate && (
              <Button size="sm" variant="ghost" loading={transferred.isPending} onClick={() => transferred.mutate({ input: { id } })}>
                {t('invoices.giroo.record')}
              </Button>
            )
          )}
          {inv.ublMessages.filter((m) => m.level === 'error').length > 0 && (
            <ul className="basis-full list-disc pl-5 text-xs text-danger">
              {inv.ublMessages
                .filter((m) => m.level === 'error')
                .slice(0, 8)
                .map((m, i) => (
                  <li key={i}>
                    <span className="font-mono">{m.code}</span> {m.text}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="grid content-start gap-6">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold">{t('invoices.lines')}</h2>
              {editable && (
                <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                  <Plus /> {t('invoices.addLine')}
                </Button>
              )}
            </div>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('invoices.fields.description')}</Th>
                  <Th className="text-right">{t('invoices.fields.quantity')}</Th>
                  <Th className="hidden text-right sm:table-cell">{t('invoices.fields.unitPrice')}</Th>
                  <Th className="hidden md:table-cell">{t('invoices.fields.vat')}</Th>
                  <Th className="text-right">{t('invoices.fields.amount')}</Th>
                  {editable && <Th className="sr-only">{t('common.actions')}</Th>}
                </Tr>
              </THead>
              <TBody>
                {inv.lines.map((l) => (
                  <Tr key={l.id}>
                    <Td>
                      {l.description}
                      <span className="block text-xs text-fg-3">
                        {t(`invoices.source.${l.source}`)}
                        {l.source === 'time' && l.durationSeconds !== null ? ` · ${formatDuration(Math.abs(l.durationSeconds))} · ${t('invoices.entries', { count: l.entryCount })}` : ''}
                        {l.workTypeName ? ` · ${l.workTypeName}` : ''}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatNumber(l.quantityMilli / 1000, me.locale, l.unit === 'hour' || l.unit === 'km' ? 2 : 0)} {t(`invoices.unit.${l.unit}`)}
                    </Td>
                    <Td className="hidden text-right tabular-nums sm:table-cell">{formatMoney(l.unitPriceCents, inv.currency)}</Td>
                    <Td className="hidden md:table-cell">{t(`invoices.vatCode.${l.vatCode}`)}</Td>
                    <Td className="text-right tabular-nums">{formatMoney(l.amountCents, inv.currency)}</Td>
                    {editable && (
                      <Td className="text-right">
                        <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => removeLine.mutate({ input: { id: l.id } })}>
                          <Trash2 />
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </TBody>
            </Table>
            <dl className="ml-auto mt-4 grid max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
              <dt className="text-fg-2">{t('invoices.totals.subtotal')}</dt>
              <dd className="text-right tabular-nums">{formatMoney(inv.subtotalCents, inv.currency)}</dd>
              {inv.vatBreakdown.map((v) => (
                <Fragment key={`${v.code}-${v.rateBp}`}>
                  <dt className="text-fg-2">
                    {t('invoices.totals.vat')} {v.rateBp / 100}% ({t(`invoices.vatCode.${v.code}`)})
                  </dt>
                  <dd className="text-right tabular-nums">{formatMoney(v.vatCents, inv.currency)}</dd>
                </Fragment>
              ))}
              <dt className="font-bold">{t('invoices.fields.total')}</dt>
              <dd className="text-right font-bold tabular-nums">{formatMoney(inv.totalCents, inv.currency)}</dd>
              {inv.kind === 'invoice' && inv.paidCents > 0 && (
                <>
                  <dt className="text-fg-2">{t('invoices.totals.paid')}</dt>
                  <dd className="text-right tabular-nums">{formatMoney(inv.paidCents, inv.currency)}</dd>
                </>
              )}
              {inv.creditedCents > 0 && (
                <>
                  <dt className="text-fg-2">{t('invoices.totals.credited')}</dt>
                  <dd className="text-right tabular-nums">{formatMoney(inv.creditedCents, inv.currency)}</dd>
                </>
              )}
              {inv.kind === 'invoice' && !draft && inv.status !== 'pending' && inv.status !== 'cancelled' && (
                <>
                  <dt className="font-bold">{t('invoices.fields.open')}</dt>
                  <dd className="text-right font-bold tabular-nums">{formatMoney(inv.openCents, inv.currency)}</dd>
                </>
              )}
            </dl>
          </Card>
          {inv.kind === 'invoice' && ['approved', 'sent', 'partially_paid', 'paid', 'credited'].includes(inv.status) && <Payments invoiceId={id} currency={inv.currency} canManage={canManage} />}
          <AttachmentsPanel type="invoice" id={id} />
        </div>
        <div className="grid content-start gap-6">
          <Card className="grid gap-3 p-4">
            <h2 className="text-lg font-bold">{t('common.details')}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('invoices.fields.issueDate')} hint={draft ? t('invoices.issueDateHint') : undefined} error={errors.issueDate}>
                {(p) => <Input {...p} type="date" value={f.issueDate} disabled={!editable} onChange={(e) => set({ issueDate: e.target.value })} />}
              </Field>
              <Field label={t('invoices.fields.paymentTermDays')} error={errors.paymentTermDays}>
                {(p) => <Input {...p} type="number" min={0} max={365} value={f.paymentTermDays} disabled={!editable} onChange={(e) => set({ paymentTermDays: e.target.value })} />}
              </Field>
            </div>
            {inv.dueDate && (
              <p className="text-sm text-fg-2">
                {t('invoices.fields.dueDate')}: <span className="font-bold text-fg-1">{formatDate(inv.dueDate)}</span>
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('invoices.fields.language')}>
                {(p) => (
                  <NativeSelect {...p} value={f.language} disabled={!editable} onChange={(e) => set({ language: e.target.value })}>
                    {INVOICE_LANGUAGES.map((l) => (
                      <option key={l} value={l}>
                        {t(`invoices.language.${l}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('invoices.fields.vatScheme')}>
                {(p) => (
                  <NativeSelect {...p} value={f.vatScheme} disabled={!editable} onChange={(e) => set({ vatScheme: e.target.value })}>
                    {VAT_SCHEMES.map((v) => (
                      <option key={v} value={v}>
                        {t(`invoices.vatScheme.${v}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </div>
            {inv.kind === 'invoice' && (
              <Field label={t('invoices.fields.grouping')} hint={t('invoices.groupingHint')}>
                {(p) => (
                  <NativeSelect {...p} value={f.grouping} disabled={!editable} onChange={(e) => set({ grouping: e.target.value })}>
                    {GROUPINGS.map((g) => (
                      <option key={g} value={g}>
                        {t(`invoices.grouping.${g}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            <Field label={t('invoices.fields.reference')} hint={t('invoices.referenceHint')} error={errors.reference}>
              {(p) => <Input {...p} value={f.reference} disabled={!editable} onChange={(e) => set({ reference: e.target.value })} />}
            </Field>
            <Field label={t('invoices.fields.purchaseOrder')} error={errors.purchaseOrder}>
              {(p) => <Input {...p} value={f.purchaseOrder} disabled={!editable} onChange={(e) => set({ purchaseOrder: e.target.value })} />}
            </Field>
            {inv.kind === 'invoice' && (
              <Field label={t('org.fields.paymentMethod')} hint={inv.directDebitMandate ? t('invoices.mandate', { mandate: inv.directDebitMandate }) : undefined}>
                {(p) => (
                  <NativeSelect {...p} value={f.paymentMethod} disabled={!editable} onChange={(e) => set({ paymentMethod: e.target.value })}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {t(`org.paymentMethod.${m}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            <Field label={t('invoices.fields.notes')}>{(p) => <Textarea {...p} rows={3} value={f.notes} disabled={!editable} onChange={(e) => set({ notes: e.target.value })} />}</Field>
            {(inv.periodFrom || inv.periodTo) && (
              <p className="text-sm text-fg-2">
                {t('invoices.fields.period')}: {inv.periodFrom ? formatDate(inv.periodFrom) : '…'} – {inv.periodTo ? formatDate(inv.periodTo) : '…'}
              </p>
            )}
            {errors._ && (
              <p role="alert" className="text-sm text-danger">
                {errors._}
              </p>
            )}
            {editable && (
              <div className="flex justify-end">
                <Button disabled={!form} loading={save.isPending} onClick={saveHeader}>
                  {t('common.save')}
                </Button>
              </div>
            )}
          </Card>
          {(inv.supplier || inv.customer) && (
            <Card className="grid gap-4 p-4 sm:grid-cols-2">
              {inv.supplier && (
                <div>
                  <h3 className="mb-1 text-sm font-bold">{t('invoices.supplier')}</h3>
                  {party(inv.supplier)}
                </div>
              )}
              {inv.customer && (
                <div>
                  <h3 className="mb-1 text-sm font-bold">{t('invoices.customer')}</h3>
                  {party(inv.customer)}
                </div>
              )}
            </Card>
          )}
          <Card className="grid gap-1 p-4 text-sm text-fg-2">
            <h2 className="text-lg font-bold text-fg-1">{t('invoices.history')}</h2>
            <span>
              {t('common.created')}: {formatDateTime(inv.createdAt)}
            </span>
            {inv.approvedAt && (
              <span>
                {t('invoices.approvedAt')}: {formatDateTime(inv.approvedAt)} {inv.approvedByName ? `(${inv.approvedByName})` : ''}
              </span>
            )}
            {inv.sentAt && (
              <span>
                {t('invoices.sentAt')}: {formatDateTime(inv.sentAt)}
              </span>
            )}
            {inv.paidAt && (
              <span>
                {t('invoices.paidAt')}: {formatDateTime(inv.paidAt)}
              </span>
            )}
            {inv.cancelledAt && (
              <span>
                {t('invoices.cancelledAt')}: {formatDateTime(inv.cancelledAt)}
              </span>
            )}
            {inv.creditNoteIds.length > 0 && (
              <span>
                {t('invoices.creditNotes')}:{' '}
                {inv.creditNoteIds.map((c) => (
                  <Link key={c} to="/billing/invoices/$id" params={{ id: c }} className="underline">
                    {t('invoices.kind.credit')}
                  </Link>
                ))}
              </span>
            )}
          </Card>
        </div>
      </div>
      {adding && <NewLineDialog invoiceId={id} vatScheme={inv.vatScheme} onClose={() => setAdding(false)} />}
      {paying && <PaymentDialog invoiceId={id} openCents={inv.openCents} onClose={() => setPaying(false)} />}
      {decide && inv.approvalId && <DecideDialog open approvalId={inv.approvalId} decision={decide} onClose={() => setDecide(null)} />}
    </>
  );
}

import { Fragment } from 'react';

function NewLineDialog({ invoiceId, vatScheme, onClose }: { invoiceId: string; vatScheme: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unit, setUnit] = useState<(typeof LINE_UNITS)[number]>('piece');
  const [price, setPrice] = useState('');
  const [vatCode, setVatCode] = useState<(typeof VAT_CODES)[number]>(vatScheme === 'reverse_charge' || vatScheme === 'outside_eu' || vatScheme === 'exempt' ? vatScheme : 'standard');
  const create = useActionMutation(createInvoiceLine, { invalidate: [['billing']], success: t('invoices.lineAdded'), onSuccess: onClose });
  const cents = inputToCents(price);
  const qty = Number(quantity.replace(',', '.'));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('invoices.addLine')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (cents === null || !Number.isFinite(qty)) return;
            create.mutate({ input: { id: invoiceId, description, quantityMilli: Math.round(qty * 1000), unit, unitPriceCents: cents, vatCode } });
          }}
        >
          <Field label={t('invoices.fields.description')}>{(p) => <Input {...p} value={description} autoFocus onChange={(e) => setDescription(e.target.value)} />}</Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('invoices.fields.quantity')}>{(p) => <Input {...p} inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />}</Field>
            <Field label={t('invoices.fields.unit')}>
              {(p) => (
                <NativeSelect {...p} value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)}>
                  {LINE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {t(`invoices.unit.${u}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('invoices.fields.unitPrice')}>{(p) => <Input {...p} inputMode="decimal" placeholder="120.00" value={price} onChange={(e) => setPrice(e.target.value)} />}</Field>
          </div>
          <Field label={t('invoices.fields.vat')}>
            {(p) => (
              <NativeSelect {...p} value={vatCode} onChange={(e) => setVatCode(e.target.value as typeof vatCode)}>
                {VAT_CODES.map((c) => (
                  <option key={c} value={c}>
                    {t(`invoices.vatCode.${c}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!description.trim() || cents === null || !Number.isFinite(qty)} loading={create.isPending}>
              {t('common.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ invoiceId, openCents, onClose }: { invoiceId: string; openCents: number; onClose: () => void }) {
  const { t } = useTranslation();
  const [paidOn, setPaidOn] = useState(today());
  const [amount, setAmount] = useState(centsToInput(openCents));
  const [source, setSource] = useState<(typeof PAYMENT_SOURCES)[number]>('bank');
  const [reference, setReference] = useState('');
  const create = useActionMutation(createPayment, { invalidate: [['billing']], success: t('invoices.paymentRegistered'), onSuccess: onClose });
  const cents = inputToCents(amount);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('invoices.registerPayment')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!cents) return;
            create.mutate({ input: { id: invoiceId, paidOn, amountCents: cents, source, reference: reference || undefined } });
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('invoices.payment.paidOn')}>{(p) => <Input {...p} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />}</Field>
            <Field label={t('invoices.fields.amount')} hint={t('invoices.payment.openHint', { amount: formatMoney(openCents) })}>
              {(p) => <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />}
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('invoices.payment.source')}>
              {(p) => (
                <NativeSelect {...p} value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                  {PAYMENT_SOURCES.filter((s) => s !== 'credit_note').map((s) => (
                    <option key={s} value={s}>
                      {t(`invoices.payment.sources.${s}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('invoices.payment.reference')}>{(p) => <Input {...p} value={reference} onChange={(e) => setReference(e.target.value)} />}</Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!cents} loading={create.isPending}>
              {t('invoices.registerPayment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Payments({ invoiceId, currency, canManage }: { invoiceId: string; currency: string; canManage: boolean }) {
  const { t } = useTranslation();
  const q = useQuery(paymentsQuery(invoiceId));
  const remove = useActionMutation(deletePayment, { invalidate: [['billing']], success: t('invoices.paymentRemoved') });
  const rows = q.data?.items ?? [];
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-lg font-bold">{t('invoices.payments')}</h2>
      {!rows.length ? (
        <p className="text-sm text-fg-3">{t('invoices.noPayments')}</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('invoices.payment.paidOn')}</Th>
              <Th>{t('invoices.payment.source')}</Th>
              <Th className="hidden sm:table-cell">{t('invoices.payment.reference')}</Th>
              <Th className="text-right">{t('invoices.fields.amount')}</Th>
              {canManage && <Th className="sr-only">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {rows.map((p) => (
              <Tr key={p.id}>
                <Td className="tabular-nums">{formatDate(p.paidOn)}</Td>
                <Td>{t(`invoices.payment.sources.${p.source}`)}</Td>
                <Td className="hidden text-fg-2 sm:table-cell">{p.reference}</Td>
                <Td className="text-right tabular-nums">{formatMoney(p.amountCents, currency)}</Td>
                {canManage && (
                  <Td className="text-right">
                    {p.source !== 'credit_note' && (
                      <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => remove.mutate({ input: { id: p.id } })}>
                        <Trash2 />
                      </Button>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
